import { OrgData } from "@p0security/shared/types/tenant/index";
import { useCallback, useEffect, useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { getConfig } from "../../../config.ts";
import { SESSION_START_ITEM_KEY } from "../../Session/useSessionTimeout.ts";
import { getCustomOidcEnabled } from "../config/customOidc";
import { AuthClient, AuthData, Tenant } from "../types/auth";
import { createNormalizedUserFromOidc } from "../utils/normalizeUser";

/**
 * Custom OIDC authentication hook using react-oidc-context.
 * Manages OIDC authentication state leveraging oidc-client-ts for token management.
 *
 * Token storage and cross-tab synchronization are handled automatically by
 * oidc-client-ts via WebStorageStateStore configured in CustomOidcProvider.
 *
 * @param tenant - Resolved tenant information
 * @param orgSlug - Organization slug for routing
 * @param orgData - Organization configuration data
 * @returns AuthClient & AuthData interface matching Firebase auth pattern
 */

export const useCustomOidcAuth = (
  tenant: Tenant,
  orgData: OrgData | undefined,
  orgSlug: string | undefined
): AuthClient & AuthData => {
  const auth = useAuth();
  const config = getConfig();

  // Determine if OIDC is enabled from runtime config
  const enabled = getCustomOidcEnabled();
  const useSubAsEmail = config.auth?.useSubAsEmail;

  // Create normalized user from OIDC profile claims
  const normalizedUser = useMemo(() => {
    if (!enabled || !auth.isAuthenticated || !auth.user?.profile) {
      return undefined;
    }
    return createNormalizedUserFromOidc(tenant, auth.user?.profile, {
      useSubAsEmail,
    });
  }, [
    enabled,
    auth.isAuthenticated,
    auth.user?.profile,
    tenant,
    useSubAsEmail,
  ]);

  const signInWithProvider = useCallback(async () => {
    if (!enabled || auth.isAuthenticated) {
      return;
    }

    if (orgSlug) {
      window.sessionStorage.setItem("p0-oidc-orgslug", orgSlug);
    }

    sessionStorage.setItem(SESSION_START_ITEM_KEY, Date.now().toString());
    await auth.signinRedirect?.();
  }, [auth, enabled, orgSlug]);

  const signOut = useCallback(async () => {
    if (!enabled || !auth.signoutRedirect) {
      return;
    }

    try {
      sessionStorage.removeItem(SESSION_START_ITEM_KEY);

      const options = auth.user?.id_token
        ? { id_token_hint: auth.user.id_token }
        : undefined;

      await auth.signoutRedirect(options);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[useCustomOidcAuth] Sign out redirect failed", { error });
    }
  }, [auth, enabled]);

  useEffect(() => {
    if (auth.error && enabled) {
      sessionStorage.removeItem(SESSION_START_ITEM_KEY);
    }
  }, [auth.error, enabled]);

  const authClient: AuthClient = {
    isInAuth: enabled ? auth.isLoading : false,
    needsEmailVerification: false,
    signInWithProvider,
    signOut,
  };

  const authData: AuthData = {
    user: normalizedUser,
    userCredential: undefined,
    tenant,
    orgSlug,
    orgData,
  };

  return {
    ...authClient,
    ...authData,
  };
};
